import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {useCallback, useEffect, useMemo, useState} from "react";

import {maxPhotoSize} from "../../constant";
import {useMobileView} from "../../hooks";
import {CloseIcon, memoWithType, TrashIcon, useConfirmModal} from "../../react";
import ButtonIconTooltip from "../ButtonIconTooltip";
import DragHandle from "../DragHandle";
import ImageViewer from "../ImageViewer";
import {type OptionType, Select} from "../Select";
import {Box} from "../ui/box";
import {Center} from "../ui/center";
import {Pressable} from "../ui/pressable";
import {Tooltip} from "../ui/tooltip";
import {VStack} from "../ui/vstack";

import ImageUploadProgress from "./ImageUploadProgress";

const MAX_WIDTH = maxPhotoSize.width;
const MAX_HEIGHT = maxPhotoSize.height;

type SupportedType = File | string;
type Props<T> = {
    file: T;
    imageAlt: string;
    progress?: number;
    className?: string;
    uploading?: boolean;
    size?: "sm" | "md" | "lg";
    onResize?: (file: {name: string; blob: Blob}) => void;
    onRemove?: (file: T) => void;
    onChangeTags?: (file: T, tags: string[]) => void;
} & StrictUnion<
    | {
          isNative?: false;
          canReorder?: boolean;
          selectedTags?: string[];
          tagOptions?: OptionType<string>[];
      }
    | {
          isNative: true;
      }
>;

const wrapperStyles = tva({
    base: "relative",
    variants: {
        size: {
            sm: "min-w-12 max-w-12",
            md: "w-full md:w-[165px]",
            lg: "w-full md:w-[270px]"
        }
    }
});
const centerContainerStyle = tva({
    base: "flex-shrink-0 flex-grow-0 overflow-hidden group",
    variants: {
        size: {
            sm: "w-12 aspect-square rounded-md bg-blueGray-50 self-start border border-solid border-blueGray-200",
            md: "w-full md:w-[165px] aspect-[3/2] rounded-lg",
            lg: "w-full md:w-[270px] aspect-[3/2] rounded-sm"
        }
    }
});
const actionStyle = tva({
    // TODO: probably make the button visible so that it work on the app and touch screen
    base:
        "absolute " +
        "top-0 " +
        "z-20 " +
        "lg:group-hover:opacity-100 " +
        "transition-opacity " +
        "duration-300 " +
        "pointer-fine:opacity-100",
    variants: {
        size: {
            md: "p-2",
            lg: "p-3"
        },
        isDragHandle: {
            true: "z-10 left-0",
            false: "z-20 right-0 lg:opacity-0"
        }
    }
});

const ImageUploadPreview = <T extends SupportedType>({
    file,
    imageAlt,
    selectedTags,
    tagOptions,
    size = "md",
    progress,
    canReorder,
    uploading,
    className,
    isNative,
    onResize,
    onRemove,
    onChangeTags
}: Props<T>) => {
    const isMobile = useMobileView();
    const [onConfirmRemoveImage] = useConfirmModal({
        title: "Remove image?",
        message: "Are you sure you want to remove the image? You can't reverse this action",
        buttonText: "Remove",
        data: file,
        onOk: onRemove
    });
    const [blob, setBlob] = useState<Blob>();

    const {loadingSize, textProgressSize, closeProgressIconSize} = useMemo(() => {
        const imageUploadPreviewSizeMap = {
            sm: {
                loadingSize: 16,
                textProgressSize: "2xs",
                closeProgressIconSize: "3xs"
            },
            md: {
                loadingSize: 20,
                textProgressSize: "xs",
                closeProgressIconSize: "sm"
            },
            lg: {
                loadingSize: 24,
                textProgressSize: "sm",
                closeProgressIconSize: "sm"
            }
        } as const satisfies Record<
            "sm" | "md" | "lg",
            {
                loadingSize: number;
                textProgressSize: "2xs" | "xs" | "sm";
                closeProgressIconSize: "3xs" | "sm";
            }
        >;
        return imageUploadPreviewSizeMap[size];
    }, [size]);

    useEffect(() => {
        if (process.env.IS_WEB) {
            if (typeof file === "string") {
                return;
            }
            const reader = new FileReader();
            reader.onload = e => {
                const img = document.createElement("img");
                img.src = e.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    if (!ctx) {
                        return;
                    }
                    ctx.drawImage(img, 0, 0);

                    // set ratio
                    let {width, height} = img;
                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                    canvas.width = width;
                    canvas.height = height;

                    const ctx1 = canvas.getContext("2d");
                    ctx1?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(blob => {
                        if (blob) {
                            setBlob(blob);
                            onResize?.({
                                blob,
                                name: file.name
                            });
                        }
                    }, file.type);
                };
            };
            // start read the file
            reader.readAsDataURL(file);
        }
    }, [file, onResize]);

    const url = useMemo(() => {
        if (typeof file === "string") {
            return file;
        }
        return blob ? URL.createObjectURL(blob) : URL.createObjectURL(file);
    }, [file, blob]);

    const handleRemoveFile = useCallback(() => {
        if (typeof file === "string" && !isNative) {
            onConfirmRemoveImage();
        } else {
            onRemove?.(file);
        }
    }, [file, isNative, onRemove, onConfirmRemoveImage]);

    const selectedTagsOption = useMemo(() => {
        if (tagOptions && selectedTags) {
            return tagOptions.filter(tag => selectedTags.includes(tag.value));
        }
    }, [selectedTags, tagOptions]);

    const handleChangeTags = useCallback(
        (tags: readonly OptionType<string>[]) => {
            onChangeTags?.(
                file,
                tags.map(tag => tag.value)
            );
        },
        [file, onChangeTags]
    );

    useEffect(() => {
        return () => {
            if (process.env.IS_WEB && !isNative) {
                URL.revokeObjectURL(url);
            }
        };
    }, [url, isNative]);

    if (size === "sm") {
        return (
            <Box className={wrapperStyles({className})} testID="image-preview">
                <Center className={centerContainerStyle({size})}>
                    <ImageViewer imageAlt={imageAlt} size={size} uri={url} />
                </Center>
                {uploading && (
                    <Box className="absolute inset-0 border border-solid border-blueGray-200 overflow-hidden z-1 rounded-md">
                        <ImageUploadProgress
                            closeProgressIconSize={closeProgressIconSize}
                            loadingSize={loadingSize}
                            progress={progress ?? 0}
                            textSize={textProgressSize}
                            onCancelUploading={handleRemoveFile}
                        />
                    </Box>
                )}
                {onRemove && !uploading && (
                    <Box className="absolute -top-2 -right-2 z-2">
                        <ButtonIconTooltip
                            className="rounded-full bg-blueGray-100"
                            icon={CloseIcon}
                            size="sm"
                            testID="btn-remove-image"
                            tooltip="Remove image"
                            onPress={handleRemoveFile}
                        />
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <VStack className={wrapperStyles({className, size})} space="sm">
            <Center className={centerContainerStyle({size})} testID="image-upload-preview">
                {uploading && (
                    <Box
                        className="absolute top-0 left-0 right-0 bottom-0 z-10"
                        testID="image-upload-progress"
                    >
                        <ImageUploadProgress
                            loadingSize={loadingSize}
                            progress={progress ?? 0}
                            textSize={textProgressSize}
                            onCancelUploading={handleRemoveFile}
                        />
                    </Box>
                )}
                <ImageViewer imageAlt={imageAlt} size={size} uri={url} />
                {onRemove && !uploading && (
                    <Box
                        className={actionStyle({size, isDragHandle: false})}
                        testID="image-upload-action"
                    >
                        <Tooltip label="Remove image" placement={isMobile ? "left" : "top"}>
                            <Pressable onPress={handleRemoveFile}>
                                <TrashIcon color="white" size="sm" />
                            </Pressable>
                        </Tooltip>
                    </Box>
                )}
                {process.env.IS_WEB && (
                    <>
                        {!uploading && (onRemove || canReorder) && (
                            <div
                                className={`
                                absolute
                                top-0
                                left-0
                                right-0
                                w-full
                                h-10
                                pointer-events-none
                                opacity-30
                                lg:opacity-0
                                group-hover:opacity-30
                                pointer-fine:opacity-30
                                bg-gradient-to-b
                                from-black
                                to-transparent
                            `}
                            />
                        )}
                        {canReorder && !uploading && (
                            <DragHandle
                                className={actionStyle({size, isDragHandle: true})}
                                iconSize="sm"
                                testID="image-upload-action"
                            />
                        )}
                    </>
                )}
            </Center>

            {process.env.IS_WEB && !isNative && tagOptions && (
                // TODO: will support on the app once the multiple select component is done
                <Select
                    isMulti
                    options={tagOptions}
                    value={selectedTagsOption}
                    onChange={handleChangeTags}
                />
            )}
        </VStack>
    );
};

export default memoWithType(ImageUploadPreview);
